import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Info,
  Zap,
  Layers,
  BookOpen,
  Compass,
} from 'lucide-react';
import Disclaimer from '../components/Disclaimer';
import AboutSelah from '../components/AboutSelah';
import RotatingScripture from '../components/RotatingScripture';
import SelahBrandMark from '../components/SelahBrandMark';
import AppStoreBadges from '../components/AppStoreBadges';
import { getDisplayPriceUSD } from '../utils/platform';

const STEPS = [
  { n: 1, title: 'Pause', body: 'Put away outside noise.' },
  { n: 2, title: 'Listen', body: "Open your spirit to the Father's voice." },
  { n: 3, title: 'Receive', body: 'Your divine download is waiting.' },
];

// New product summary per pivot: restful, midnight-ready utility
const PRODUCT_FEATURES = [
  {
    icon: Zap,
    title: '24/7 Flash Capture',
    body: 'Voice or text — capture spiritual flashes the moment they arrive, day or 3:00 AM. No friction, no forgetting.',
  },
  {
    icon: Layers,
    title: 'AI Auto-Categorization Engine',
    body: 'Each download is quietly sorted — Finance, Family, Education, Love & Connection, Divine Purpose and more — so patterns can surface on their own.',
  },
  {
    icon: BookOpen,
    title: 'Dynamic Scriptural Mapping',
    body: 'Every entry is matched to living Scripture, insights, and an activation prayer so revelation moves from impression to action.',
  },
  {
    icon: Compass,
    title: 'Purpose & Next Steps Tracker',
    body: 'Follow-ups build a timeline. Over weeks, your Insight Map reveals what the Holy Spirit keeps highlighting — and the next step to walk in it.',
  },
];

const Welcome = () => {
  const navigate = useNavigate();

  const handleEnter = () => {
    navigate('/register');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 hero-gradient" />

        {/* Top hero + right-column on desktop */}
        <div className="relative z-10 max-w-6xl mx-auto w-full px-4 sm:px-8 pt-10 lg:pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* LEFT: Logo + Title + CTA */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <div className="flex justify-center lg:justify-start mb-5">
                <SelahBrandMark size={100} sparkleSize={22} />
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-brand text-[var(--sd-fg)] leading-tight mb-3">
                A restful place for your <span className="text-[var(--sd-primary-dark)]">Selah</span>
              </h1>
              <p className="text-lg sm:text-xl text-[var(--sd-muted-fg)] leading-relaxed max-w-xl mx-auto lg:mx-0 mb-4">
                Capture spiritual flashes the moment they arrive. Let AI shape each one into structured insights, Scripture, and a personal prayer — meant to accompany your walk, not measure it.
              </p>
              <p className="text-sm sm:text-base text-[var(--sd-fg-secondary)] max-w-xl mx-auto lg:mx-0 mb-7 italic">
                Built for 3:00 AM revelation, morning coffee stirrings, and everything in between.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start items-center">
                <Button
                  onClick={handleEnter}
                  size="lg"
                  className="bg-[var(--sd-primary)] hover:bg-[#D7A1A8] text-[var(--sd-primary-fg)] font-semibold text-lg px-10 py-6 rounded-xl shadow-lg"
                  data-testid="welcome-enter-button"
                >
                  Enter Your Quiet
                </Button>
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm text-[var(--sd-primary-dark)] hover:underline font-medium"
                >
                  Already have an account? Sign in
                </button>
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-center lg:justify-start gap-2 text-sm">
                <span className="inline-flex items-center gap-1.5 bg-[var(--sd-primary)]/10 text-[var(--sd-primary-dark)] px-3 py-1.5 rounded-full border border-[var(--sd-primary)]/30 font-medium">
                  <Sparkles size={14} />
                  7-day free trial, then ${getDisplayPriceUSD().toFixed(2)}/mo
                </span>
                <span className="text-[var(--sd-muted-fg)] text-xs">No credit card required</span>
              </div>

              <p className="mt-3 flex items-start gap-2 text-xs text-[var(--sd-muted-fg)] max-w-md mx-auto lg:mx-0 leading-relaxed">
                <Info size={13} className="flex-shrink-0 mt-0.5 text-[var(--sd-primary)]" />
                Trial auto-cancels after 7 days unless you subscribe. Your downloads
                stay saved and available if you resubscribe later.
              </p>

              {/* Mobile app store badges — quiet inline pair under the hero CTA.
                  Only renders on web; hidden inside wrapped iOS/Android builds. */}
              <div className="mt-6">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--sd-primary-dark)] font-semibold mb-3">
                  Now on iPhone &amp; Android
                </p>
                <AppStoreBadges size="md" align="start" />
              </div>
            </motion.div>

            {/* RIGHT: 3 steps + scripture */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-5"
            >
              <div className="glass-card p-6 sm:p-8">
                <h2 className="text-xs font-bold uppercase tracking-wider text-[var(--sd-primary-dark)] mb-4">
                  The rhythm
                </h2>
                <div className="space-y-4">
                  {STEPS.map((s, i) => (
                    <motion.div
                      key={s.n}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 0.5 + i * 0.15 }}
                      className="flex items-start gap-4"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--sd-primary)]/15 border border-[var(--sd-primary)]/40 flex items-center justify-center">
                        <span className="text-base font-semibold text-[var(--sd-primary-dark)]">
                          {s.n}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-[var(--sd-fg)] font-scripture">
                          {s.title}
                        </h3>
                        <p className="text-sm text-[var(--sd-muted-fg)]">{s.body}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              <RotatingScripture
                scriptureKey="welcome-hero"
                mood="rest"
                variant="card"
                className="p-5"
              />
            </motion.div>
          </div>
        </div>

        {/* NEW: Product Summary — "What's inside" */}
        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-8 pt-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-[var(--sd-primary-dark)] font-semibold mb-2">
              A midnight-ready utility
            </p>
            <h2 className="text-3xl sm:text-4xl font-brand text-[var(--sd-fg)] mb-3">
              What's inside Selah Downloads
            </h2>
            <p className="text-base text-[var(--sd-fg-secondary)] max-w-2xl mx-auto">
              Four quiet tools that carry a spark from the moment it arrives all the way into the next step you can actually take.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {PRODUCT_FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="glass-card p-5 sm:p-6"
                  data-testid={`welcome-feature-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-[var(--sd-primary)]/12 border border-[var(--sd-primary)]/35 flex items-center justify-center">
                      <Icon size={20} className="text-[var(--sd-primary-dark)]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[var(--sd-fg)] mb-1">{f.title}</h3>
                      <p className="text-sm text-[var(--sd-fg-secondary)] leading-relaxed">{f.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* About Selah — Why / IS / IS NOT */}
        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-8 pt-14 pb-4">
          <AboutSelah variant="full" />
        </div>

        {/* ── Dedicated "Get the App" section ────────────────────────────
            Prominent download call-to-action with both official store badges.
            Only renders on web (compliance-gated inside wrapped builds). */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-8 pt-8 pb-16"
          data-testid="welcome-get-the-app"
        >
          <div className="glass-card p-8 sm:p-12 relative overflow-hidden">
            {/* Rose-gold accent glow */}
            <div
              aria-hidden
              className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full"
              style={{
                background:
                  'radial-gradient(circle, rgba(183,110,121,0.18) 0%, transparent 65%)',
                filter: 'blur(30px)',
              }}
            />

            <div className="relative flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
              {/* Copy */}
              <div className="flex-1 text-center lg:text-left">
                <p className="text-xs uppercase tracking-[0.24em] text-[var(--sd-primary-dark)] font-semibold mb-3">
                  Available now
                </p>
                <h2 className="text-3xl sm:text-4xl font-brand text-[var(--sd-fg)] mb-3 leading-tight">
                  Get Selah Downloads on your phone
                </h2>
                <p className="text-base text-[var(--sd-fg-secondary)] leading-relaxed max-w-xl mx-auto lg:mx-0">
                  Capture the flash the moment it arrives — voice or written —
                  from your iPhone or Android. Everything syncs quietly to your
                  web account. Encrypted end-to-end.
                </p>
              </div>

              {/* Badges */}
              <div className="flex-shrink-0">
                <AppStoreBadges size="lg" align="center" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <Disclaimer />
    </div>
  );
};

export default Welcome;
